<template>
  <div>
    <div class="fs22">
      {{ $t("incomeChart.title") }}1
    </div>
    <ProgressLoader
      v-if="loading"
      mode="indeterminate"
      color="#5AC396"
      size="150px"
    />
    <div v-else>
      <div class="i-control">
        <div class="i-indicator">
          <AuButton
            :bordered="false"
            font-color="#636363"
            font-size="13px"
            height="28px"
            padding-horizontal="10px"
            :type="isBalanceActive ? 'link' : ''"
            @click="selectGraph"
          >
            {{ $t("incomeChart.balance") }}
          </AuButton>
          <AuButton
            :bordered="false"
            font-color="#636363"
            font-size="13px"
            height="28px"
            padding-horizontal="10px"
            :type="!isBalanceActive ? 'link' : ''"
            @click="selectGraph"
          >
            {{ $t("incomeChart.profit") }}
          </AuButton>
        </div>
        <div class="i-indicator">
          <AuButton
            :bordered="false"
            font-color="#636363"
            font-size="13px"
            height="28px"
            padding-horizontal="10px"
            :type="activePeriod === 0 ? 'link' : ''"
            @click="activePeriod = 0"
          >
            {{ $t("incomeChart.day") }}
          </AuButton>
        </div>
      </div>
      <Apexchart
        ref="chart"
        :series="series"
        :options="options"
        height="200px"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import CurrencySupport from "@/help/currencySupport";
import moment from "moment";
import ProgressLoader from "@/loaders/progress-bar";
import { formatDecimalStatic } from "../../help/decimalStatic";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "IncomeChart",
  components: { ProgressLoader },
  data() {
    return {
      currencySup: new CurrencySupport(),
      isBalanceActive: true,
      activePeriod: 0,
      minY: 0,
      maxY: 1000,
      minAssetsY: 0,
      maxAssetsY: 10000,
      minInvestY: 0,
      maxInvestY: 10000,
      balanceData: [],
      profitData: [],
      page: 1,
      itemsPerPage: 10,
      series: [
        {
          data: [2500, 2131, 2700, 1945, 1800, 1763],
          color: "#28E593",
          name: this.$t("incomeChart.balance")
        }
      ],
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      loading: true
    };
  },
  computed: {
    ...mapGetters({
      tokens: "tokens",
      dailyStatsBack: "dailyStatsBack",
      auOwners: "auOwners",
      defaultTokenMnemonic: "defaultToken/defaultTokenMnemonic"
    }),

    defaultTokenDecimals() {
      return _.find(this.tokens, token => token.mnemonicEng === this.defaultTokenMnemonic)?.decimals ?? 2;
    },

    options() {
      return {
        chart: {
          type: "area",
          toolbar: {
            show: false
          },
          parentHeightOffset: 0
        },
        stroke: {
          curve: "smooth"
        },
        markers: {
          size: 5
        },
        fill: {
          type: "gradient",
          colors: ["#28E593"],
          gradient: {
            opacityFrom: 0.7,
            opacityTo: 0.1
          }
        },
        tooltip: {
          enabled: true,
          theme: this.isLightTheme ? "light" : "dark"
        },
        dataLabels: {
          enabled: false
        },
        grid: {
          show: false,
          row: {
            colors: ["#28E5931A"],
            opacity: 0.5
          }
        },
        xaxis: {
          categories: this.categories
        },
        yaxis: {
          min: this.minY,
          max: this.maxY,
          tickAmount: 4,
          labels: {
            formatter: (value) => {
              const formattedValue = value.toFixed(2);
              return this.defaultTokenMnemonic
                ? `${formattedValue} ${this.defaultTokenMnemonic}`
                : formattedValue;
            }
          }
        }
      };
    },
    showFakeGraph() {
      return /fake/i.test(window.location.href);
    },

    isLightTheme() {
      return this.$store.getters.isLightTheme;
    }
  },

  async mounted() {
    if (!this.showFakeGraph) {
      await this.getBriefcaseData();
    }
    this.loading = false;
  },

  methods: {
    ...mapActions({
      getDailyStats: "getDailyStats"
    }),

    async getBriefcaseData() {
      const reverseData = [];
      for (let i = this.dailyStatsBack?.length ?? 0; i > 0; i--) {
        reverseData.push(this.dailyStatsBack[i - 1]);
      }

      this.categories = _.map(reverseData, (item) =>
        moment(item?.createdAt).format("DD.MM.YYYY")
      );

      this.profitData = _.map(reverseData, (item) =>
        formatDecimalStatic({
          value: item?.activeOrders,
          decimal: this.defaultTokenDecimals
        })
          .replace(/\s/g, "")
      );

      const minValueProfit = Math.min(...this.profitData);
      this.minInvestY = Math.floor(minValueProfit);
      const maxValueProfit = Math.max(...this.profitData);
      this.maxInvestY = Math.ceil(maxValueProfit);

      const balanceDataRaw = _.map(reverseData, (item) =>
        formatDecimalStatic({
          value: item?.briefcaseValue,
          decimal: this.defaultTokenDecimals
        })
          .replace(/\s/g, "")
      );
      this.balanceData = this.sumArrays(this.profitData, balanceDataRaw);

      const minValue = Math.min(...this.balanceData);
      this.minY = this.minAssetsY = Math.floor(minValue);
      const maxValue = Math.max(...this.balanceData);
      this.maxY = this.maxAssetsY = Math.ceil(maxValue);

      this.series[0].data = this.balanceData;
    },

    sumArrays(profitData, balanceDataRaw) {
      return profitData.map((profitStr, i) => {
        const profit = parseFloat(profitStr);
        const balance = parseFloat(balanceDataRaw[i]);
        const sum = (profit + balance).toFixed(2);
        return sum;
      });
    },

    selectGraph() {
      this.isBalanceActive = !this.isBalanceActive;
      if (this.showFakeGraph) {
        this.createFakeData();
      }
      this.minY = this.isBalanceActive
        ? this.minAssetsY
        : this.minInvestY;
      this.maxY = this.isBalanceActive
        ? this.maxAssetsY
        : this.maxInvestY;
      this.series[0].data = this.isBalanceActive
        ? this.balanceData
        : this.profitData;
      this.series[0].name = this.isBalanceActive
        ? this.$t("incomeChart.balance")
        : this.$t("incomeChart.profit");

      // при переключении графиков заставить его обновить данные оси Y корректно
      this.$nextTick(() => {
        this.$refs.chart.updateOptions({
          yaxis: {
            tickAmount: 4,
            min: this.minY,
            max: this.maxY,
            labels: {
              formatter: (value) => {
                const formattedValue = value.toFixed(2);
                return this.defaultTokenMnemonic
                  ? `${formattedValue} ${this.defaultTokenMnemonic}`
                  : formattedValue;
              }
            }
          }
        });
      });
    },

    createFakeData() {
      const initArray = this.isBalanceActive
        ? [2500, 2131, 2700, 1945, 1800, 1763]
        : [7, 10, 50, 40, 30, 60];
      const max = this.isBalanceActive ? 2000 : 10;
      this.series[0].data = _.map(initArray, (el) => {
        const delta = Math.floor(Math.random() * max);
        return Math.floor(Math.random() * 2) === 0 ? el + delta : el - delta;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.i-control {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.i-indicator {
  display: flex;
  gap: 10px;
}
</style>